import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSuperAdminAuth } from '../../../context/SuperAdminAuthContext';
import SEO from '../../../components/common/SEO';
import './SuperAdminLogin.css';

function FirstTimePasswordReset() {
  const { superAdmin, updateSuperAdminData } = useSuperAdminAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ text: 'Please fill in all fields.', type: 'error' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ text: 'New password must be at least 6 characters long.', type: 'error' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ text: 'New password and confirm password do not match.', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('superAdminToken');
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
      
      const response = await axios.post(
        `${API_BASE}/superadmin/first-time-password-reset`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage({ text: response.data.message || 'Password reset successful!', type: 'success' });
      
      updateSuperAdminData({ must_change_password: false });

      setTimeout(() => {
        navigate('/superadmin/dashboard');
      }, 1200);
    } catch (err) {
      const errorText = err.response?.data?.message || 'Failed to update password. Please try again.';
      setMessage({ text: errorText, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="superadmin-auth-container">
      <SEO
        title="First Login Password Reset | Mangoloops"
        description="Password reset required for new employee account."
      />

      <div className="superadmin-auth-card">
        <img
          src="/logo-mangoloops.png"
          alt="Mangoloops Logo"
          className="superadmin-logo"
        />

        <h2 className="superadmin-heading">Account Password Setup Required</h2>
        <p className="superadmin-subheading">
          Welcome {superAdmin?.name || 'Employee'}! For security reasons, you must change your temporary password before continuing.
        </p>

        {message.text && (
          <div className={`superadmin-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <div className="superadmin-input-group">
            <label>Current / Temporary Password</label>
            <input
              type="password"
              className="superadmin-input"
              placeholder="Enter temporary password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="superadmin-input-group">
            <label>New Password</label>
            <input
              type="password"
              className="superadmin-input"
              placeholder="Minimum 6 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="superadmin-input-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              className="superadmin-input"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <button
            type="submit"
            className="superadmin-btn"
            disabled={loading || message.type === 'success'}
          >
            {loading ? 'Updating Password...' : 'Save New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default FirstTimePasswordReset;
